import {
  laptopMax,
  laptopMin,
  laptopSmallMax,
  largeTabletMax,
  mobileMax,
  mobileXLargeMax,
  tabletMax,
  tabletMin as tabletMinBase,
} from '@propertypal/shared/src/constants/mediaQueries';
import CurvedLineSvg from '@propertypal/shared/src/resources/images/estate-agent-websites/curved-border-01.svg';
import CurvedLineSvgMob from '@propertypal/shared/src/resources/images/estate-agent-websites/curved-border-02.svg';
import { Content as PageWrapper } from '@propertypal/web-ui/src/layout/PageWrapper.style';
import { MidHeading, SubHeading, Text } from '@propertypal/web-ui/src/typography';
import styled, { css } from 'styled-components';

const tabletMin = tabletMinBase.replace('768', '769');

const STROKE_WIDTH = 8;
const LAPTOP_STROKE_WIDTH = 6;
const DASHED_LINE_WIDTH = 25;
const CURVED_LINE_W = 140;
const GAP = CURVED_LINE_W + 15;
const LAPTOP_GAP = CURVED_LINE_W - 10;
const CHECK_SIZE = 20;

export const Container = styled.div`
  position: relative;
  width: 100%;
  padding: 100px 0;

  ${PageWrapper} {
    max-width: 1300px;
  }

  ${SubHeading} {
    line-height: 1.1;

    @media (${tabletMax}) {
      font-size: 40px;
    }
  }

  ${Text} {
    line-height: 1.5;
  }
`;

export const ListTitle = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5px;
  margin-bottom: 25px;

  ${MidHeading} {
    color: ${({ theme }) => theme.primary};
    display: inline-flex;
    font-size: 40px;
    line-height: 1.1;

    @media (${largeTabletMax}) {
      font-size: 28px;
    }

    @media (${mobileMax}) {
      font-size: 24px;
    }
  }

  ${Text} {
    font-size: 20px;

    @media (${largeTabletMax}) {
      font-size: 16px;
    }
  }
`;

export const ListItems = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
`;

export const ListItem = styled.li`
  font-size: ${CHECK_SIZE}px;
  margin: 15px 0;
  display: flex;
  gap: 10px;
  align-items: center;

  &:first-child {
    margin-top: 0px;
  }
  &:last-child {
    margin-bottom: 0px;
  }

  svg {
    border: 1px solid ${({ theme }) => theme.primary};
    border-radius: 100%;
    width: ${CHECK_SIZE}px;
    height: ${CHECK_SIZE}px;
    padding: 3px;
  }

  span {
    flex: 1;
  }

  @media (${largeTabletMax}) {
    font-size: 16px;
    svg {
      width: 16px;
      height: 16px;
      padding: 2px;
    }
  }
`;

export const Essential = styled.div<{
  dev?: boolean;
}>`
  ${ListItems} {
    overflow: hidden;

    ${({ dev, theme }) =>
      !dev
        ? css`
            @media (${tabletMin}) {
              padding-right: ${DASHED_LINE_WIDTH}px;
              &:after {
                content: '';
                position: absolute;
                right: -${STROKE_WIDTH - 3}px;
                top: -${STROKE_WIDTH - 3}px;
                bottom: -${STROKE_WIDTH - 3}px;
                width: ${DASHED_LINE_WIDTH}px;
                border: ${STROKE_WIDTH}px dashed ${theme.primary};
                border-left-width: 0px;
              }
            }

            @media (${laptopMax}) and (${tabletMin}) {
              &:after {
                right: -${LAPTOP_STROKE_WIDTH - 2}px;
                top: -${LAPTOP_STROKE_WIDTH - 2}px;
                bottom: -${LAPTOP_STROKE_WIDTH - 2}px;
                border: ${LAPTOP_STROKE_WIDTH}px dashed ${theme.primary};
                border-left-width: 0px;
              }
            }
          `
        : ''}
  }
`;

export const Premium = styled.div`
  ${ListItems} {
    @media (${tabletMin}) {
      padding-left: ${DASHED_LINE_WIDTH}px;
      &:after {
        content: '';
        position: absolute;
      }
    }
  }
`;

export const Lists = styled.div<{ dev?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  justify-content: center;
  gap: ${GAP}px;
  width: 100%;
  padding: 0;
  margin: 50px auto 0;

  ${Essential}, ${Premium} {
    flex-basis: calc((100% - (2 - 1) * ${GAP}px) / 2);

    @media (${tabletMax}) {
      flex-basis: 100%;
    }
  }

  @media (${laptopMax}) {
    gap: ${LAPTOP_GAP}px;
  }

  @media (${tabletMax}) {
    gap: ${({ dev }) => (dev ? 15 : 40)}px;
  }
`;

export const PremiumSubtitle = styled.p`
  margin: 30px 0;
  font-size: 20px;
  position: relative;

  @media (${tabletMin}) {
    padding-left: ${DASHED_LINE_WIDTH - CHECK_SIZE}px;
  }
`;

const curvedLine = css`
  position: absolute;
  top: 0;
  top: 12px;
`;

export const CurvedLine = styled(CurvedLineSvg)`
  ${curvedLine}
  left: -${GAP}px;
  width: ${GAP}px;
  display: none;

  @media (${laptopMin}) {
    display: block;
  }
`;

export const CurvedLineMob = styled(CurvedLineSvgMob)`
  ${curvedLine}
  left: -${LAPTOP_GAP}px;
  width: ${LAPTOP_GAP}px;
  display: block;

  @media (${laptopMin}), (${tabletMax}) {
    display: none;
  }
`;

export const DevHeading = styled(MidHeading)`
  color: ${({ theme }) => theme.primary};

  @media (${laptopSmallMax}) {
    font-size: 32px;
  }

  @media (${tabletMax}) {
    ${Text} {
      display: block;
    }
  }

  @media (${mobileXLargeMax}) {
    font-size: 23px;
    ${Text} {
      font-size: 16px;
    }
  }
`;
